/** Max digits so Number stays safe and UI stays reasonable for PKR sale prices. */
const MAX_PKR_DIGITS = 12;

const BELOW_TWENTY = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];

const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

function belowHundredWords(n: number): string {
  if (n < 20) return BELOW_TWENTY[n];
  const t = Math.floor(n / 10);
  const u = n % 10;
  return u ? `${TENS[t]} ${BELOW_TWENTY[u]}` : TENS[t];
}

function belowThousandWords(n: number): string {
  if (n < 100) return belowHundredWords(n);
  const h = Math.floor(n / 100);
  const r = n % 100;
  const head = `${BELOW_TWENTY[h]} hundred`;
  return r ? `${head} ${belowHundredWords(r)}` : head;
}

/** Strip to digits only, cap length, drop leading zeros unless value is exactly "0". */
export function sanitizePkrDigits(input: string): string {
  const d = input.replace(/\D/g, "").slice(0, MAX_PKR_DIGITS);
  if (!d) return "";
  const trimmed = d.replace(/^0+/, "");
  return trimmed === "" ? "0" : trimmed;
}

/** Western-style grouping for sale price display: 3,400,000 */
export function formatAmountCommas(digits: string): string {
  if (!digits) return "";
  const n = Number(digits);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-US").format(n);
}

/** Western comma formatting for a numeric sale price (read-only). */
export function formatSalePriceNumber(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "";
  return new Intl.NumberFormat("en-US").format(Math.floor(n));
}

/** Compact PKR label: 3,400,000 → 34 Lac; 20,000,000 → 2 Crore; 34,400,000 → 3.44 Crore */
export function pkrAmountShortLabel(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "";

  const crore = 10_000_000;
  const lac = 100_000;
  const thousand = 1_000;

  const formatUnit = (value: number, unit: string): string => {
    const rounded = Math.round(value * 100) / 100;
    const text = Number.isInteger(rounded)
      ? String(rounded)
      : rounded.toFixed(2).replace(/\.?0+$/, "");
    return `${text} ${unit}`;
  };

  if (n >= crore) {
    return formatUnit(n / crore, "Crore");
  }
  if (n >= lac) {
    return formatUnit(n / lac, "Lac");
  }
  if (n >= thousand) {
    return formatUnit(n / thousand, "Th");
  }

  return new Intl.NumberFormat("en-US").format(Math.floor(n));
}

/** Digit count in `s` strictly before index `caret` (for formatted amount strings). */
export function digitsBeforeCaretInFormatted(s: string, caret: number): number {
  let n = 0;
  const end = Math.min(caret, s.length);
  for (let i = 0; i < end; i++) {
    if (/\d/.test(s[i]!)) n++;
  }
  return n;
}

/** Caret index in `formatted` so exactly `digitCount` digits lie before it. */
export function caretPositionForDigitCount(formatted: string, digitCount: number): number {
  const maxDigits = [...formatted].filter((c) => /\d/.test(c)).length;
  const target = Math.min(Math.max(0, digitCount), maxDigits);
  if (target <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]!)) {
      seen++;
      if (seen === target) return i + 1;
    }
  }
  return formatted.length;
}

/** Indian-style grouping: 1,23,45,678 */
export function formatPkrIndianCommas(digits: string): string {
  if (!digits) return "";
  const n = Number(digits);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-IN").format(n);
}

/** `en-IN` grouping for a numeric amount (read-only total line). */
export function formatPkrIndianNumber(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "";
  return new Intl.NumberFormat("en-IN").format(Math.floor(n));
}

const MAX_CRORE_DIGITS = 4;

/** Digits only for Crore field (0–9999). */
export function sanitizePkrCroreField(s: string): string {
  return s.replace(/\D/g, "").slice(0, MAX_CRORE_DIGITS);
}

/** Lac segment: digits only, max 2 characters (values clamped when building total). */
export function sanitizePkrLacField(s: string): string {
  return s.replace(/\D/g, "").slice(0, 2);
}

/** Thousands segment: digits only, max 2 characters (values clamped when building total). */
export function sanitizePkrThousandsField(s: string): string {
  return s.replace(/\D/g, "").slice(0, 2);
}

/** Last segment 0–999 (hundreds through units; e.g. 555 → 12,33,35,555). */
export function sanitizePkrHundredsField(s: string): string {
  return s.replace(/\D/g, "").slice(0, 3);
}

export function parsePkrPartField(s: string): number {
  const t = s.replace(/\D/g, "");
  if (!t) return 0;
  const n = Number(t);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/** PKR total from Indian-style parts (crore / lac / thousand / last 0–999). */
export function buildPkrFromIndianParts(crore: number, lac: number, thousands: number, last: number): number {
  const c = Math.min(Math.max(0, Math.floor(crore)), 9999);
  const l = Math.min(Math.max(0, Math.floor(lac)), 99);
  const t = Math.min(Math.max(0, Math.floor(thousands)), 99);
  const h = Math.min(Math.max(0, Math.floor(last)), 999);
  const total = c * 10000000 + l * 100000 + t * 1000 + h;
  return Number.isFinite(total) ? total : 0;
}

export function buildPkrFromPartStrings(crore: string, lac: string, thousands: string, hundreds: string): number {
  return buildPkrFromIndianParts(
    parsePkrPartField(crore),
    parsePkrPartField(lac),
    parsePkrPartField(thousands),
    parsePkrPartField(hundreds),
  );
}

export function parsePkrDigits(digits: string): number | null {
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) ? n : null;
}

/** e.g. "Forty lac twelve thousand five hundred rupees" */
export function pkrAmountInWords(n: number): string {
  if (!Number.isFinite(n) || n < 0 || n > Number.MAX_SAFE_INTEGER) return "";
  if (n === 0) return "Zero rupees";

  const crore = Math.floor(n / 10000000);
  let r = n % 10000000;
  const lac = Math.floor(r / 100000);
  r %= 100000;
  const thousand = Math.floor(r / 1000);
  r %= 1000;

  const parts: string[] = [];
  if (crore) parts.push(`${belowHundredWords(crore)} crore`);
  if (lac) parts.push(`${belowHundredWords(lac)} lac`);
  if (thousand) parts.push(`${belowThousandWords(thousand)} thousand`);
  if (r) parts.push(belowThousandWords(r));

  const head = parts.join(" ");
  return `${head.charAt(0).toUpperCase()}${head.slice(1)} rupees`;
}

/** Western comma formatting with up to 2 decimal places. */
export function formatMoneyDecimalDisplay(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "";
  if (n === 0) return "0";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Format user typing with commas and at most 2 decimal places. */
export function formatMoneyInputTyping(raw: string): string {
  const cleaned = raw.replace(/,/g, "");
  if (!cleaned) return "";

  const dotIndex = cleaned.indexOf(".");
  const wholeRaw = (dotIndex >= 0 ? cleaned.slice(0, dotIndex) : cleaned).replace(/\D/g, "");
  const decRaw = (dotIndex >= 0 ? cleaned.slice(dotIndex + 1) : "").replace(/\D/g, "").slice(0, 2);
  const formattedWhole = wholeRaw ? formatAmountCommas(wholeRaw) : "";

  if (dotIndex >= 0) {
    return decRaw.length > 0 ? `${formattedWhole}.${decRaw}` : `${formattedWhole}.`;
  }
  return formattedWhole;
}

export function parseMoneyDecimalInput(raw: string): number {
  const cleaned = raw.replace(/,/g, "").trim();
  if (!cleaned || cleaned === ".") return 0;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

/** Compact phrase next to a label (no trailing “rupees”). */
export function pkrAmountInWordsInline(n: number): string {
  if (!Number.isFinite(n) || n < 0 || n > Number.MAX_SAFE_INTEGER) return "";
  if (n === 0) return "Zero";
  return pkrAmountInWords(n).replace(/\s+rupees$/i, "").trim();
}

export type PkrAmountUnitKey = "crore" | "lac" | "thousand" | "hundred" | "rest";

/** Coloured inline breakdown: `2` crore · `60` lac · `12` thousand · `5` hundred · `67` (rest digits). */
export type PkrAmountColoredPart = {
  key: string;
  amount: string;
  /** Unit word (empty for trailing 1–99 after hundreds). */
  unit: string;
  unitKey: PkrAmountUnitKey;
};

export function pkrAmountColoredParts(n: number): PkrAmountColoredPart[] {
  if (!Number.isFinite(n) || n <= 0 || n > Number.MAX_SAFE_INTEGER) return [];

  const fmt = (x: number) => new Intl.NumberFormat("en-IN").format(x);
  const parts: PkrAmountColoredPart[] = [];

  let rem = Math.floor(n);
  const crore = Math.floor(rem / 10000000);
  rem %= 10000000;
  const lac = Math.floor(rem / 100000);
  rem %= 100000;
  const thousand = Math.floor(rem / 1000);
  rem %= 1000;

  if (crore) parts.push({ key: "cr", amount: fmt(crore), unit: "crore", unitKey: "crore" });
  if (lac) parts.push({ key: "lc", amount: fmt(lac), unit: "lac", unitKey: "lac" });
  if (thousand) parts.push({ key: "th", amount: fmt(thousand), unit: "thousand", unitKey: "thousand" });
  if (rem >= 100) {
    const h = Math.floor(rem / 100);
    const sub = rem % 100;
    parts.push({ key: "hu", amount: fmt(h), unit: "hundred", unitKey: "hundred" });
    if (sub) parts.push({ key: "rst", amount: fmt(sub), unit: "", unitKey: "rest" });
  } else if (rem > 0) {
    parts.push({ key: "rst", amount: fmt(rem), unit: "", unitKey: "rest" });
  }

  return parts;
}

export type PkrBreakdownPart = { key: string; label: string };

/** Non-zero chunks for chip row: "40 lac", "12 thousand", "500" */
export function pkrAmountBreakdown(n: number): PkrBreakdownPart[] {
  if (!Number.isFinite(n) || n <= 0) return [];

  const crore = Math.floor(n / 10000000);
  let r = n % 10000000;
  const lac = Math.floor(r / 100000);
  r %= 100000;
  const thousand = Math.floor(r / 1000);
  r %= 1000;

  const out: PkrBreakdownPart[] = [];
  if (crore) out.push({ key: "crore", label: `${new Intl.NumberFormat("en-IN").format(crore)} crore` });
  if (lac) out.push({ key: "lac", label: `${new Intl.NumberFormat("en-IN").format(lac)} lac` });
  if (thousand) out.push({ key: "thousand", label: `${new Intl.NumberFormat("en-IN").format(thousand)} thousand` });
  if (r) {
    if (r >= 100) {
      const h = Math.floor(r / 100);
      const rem = r % 100;
      out.push({ key: "hundred", label: `${h} hundred${rem ? ` ${belowHundredWords(rem)}` : ""}` });
    } else {
      out.push({ key: "rest", label: belowHundredWords(r) });
    }
  }
  return out;
}
